import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Info,
  RefreshCw,
  Route,
  Search,
  Users,
} from 'lucide-react';
import { SkeletonPage } from '../ui/kit';
import './Ops.css';
import PageHeader from '../components/PageHeader';
import { uhrzeit } from '../lib/zeit';

const integer = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const currency = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const formatInteger = (value) => integer.format(safeNumber(value));
const formatOptional = (value, formatter, suffix = '') => (
  value == null ? '–' : `${formatter.format(Number(value))}${suffix}`
);

const alertIcon = {
  danger: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
};

function MetricCard({ label, value, detail, icon: Icon, tone = 'default' }) {
  return (
    <article className={`ops-metric ops-tone-${tone}`}>
      <div className="ops-metric-top">
        <span>{label}</span>
        <span className="ops-metric-icon"><Icon size={17} strokeWidth={1.8} /></span>
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function WindowMetric({ label, value, detail }) {
  return (
    <div className="ops-window-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function SignalRow({ label, value, detail, max, tone = 'ink' }) {
  const width = max > 0 ? Math.max(4, Math.min((value / max) * 100, 100)) : 0;
  return (
    <div className="ops-signal">
      <div className="ops-signal-copy">
        <span>{label}<small>{detail}</small></span>
        <strong>{formatInteger(value)}</strong>
      </div>
      <div className="ops-signal-track" aria-hidden="true">
        <span className={`ops-signal-fill is-${tone}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function OperationalAlerts({ alerts }) {
  if (alerts.length === 0) {
    return (
      <div className="ops-clear-state">
        <span><CheckCircle2 size={18} /></span>
        <div>
          <strong>Keine operativen Hinweise</strong>
          <small>Der Server meldet aktuell keine Ausnahme.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="ops-alert-list">
      {alerts.map((alert, index) => {
        const Icon = alertIcon[alert.level] || Info;
        return (
          <div className={`ops-alert is-${alert.level || 'info'}`} key={`${alert.message}-${index}`}>
            <span className="ops-alert-icon"><Icon size={16} /></span>
            <div>
              <strong>{alert.level === 'danger' ? 'Kritisch' : alert.level === 'warning' ? 'Beobachten' : 'Hinweis'}</strong>
              <small>{alert.message}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Ops() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const response = await api.get('/admin/ops-pulse');
      setData(response.data);
      setError('');
      setLastUpdated(new Date());
    } catch {
      setError('Der Live-Betrieb konnte nicht aktualisiert werden.');
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load({ silent: true }), 9000);
    return () => clearInterval(interval);
  }, [load]);

  if (!data && !error) return <SkeletonPage />;

  if (!data) {
    return (
      <section className="ops-unavailable" role="alert">
        <AlertTriangle size={22} />
        <div>
          <strong>Live-Betrieb nicht verfügbar</strong>
          <p>{error}</p>
        </div>
        <button type="button" onClick={() => load()} disabled={refreshing}>
          <RefreshCw size={15} /> Erneut versuchen
        </button>
      </section>
    );
  }

  const now = data.now || {};
  const windowData = data.window || {};
  const series = Array.isArray(data.series) ? data.series : [];
  const alerts = Array.isArray(data.alerts) ? data.alerts : [];
  const onlineFree = safeNumber(now.online_free);
  const onlineBusy = safeNumber(now.online_busy);
  const onlineTotal = onlineFree + onlineBusy;
  const searching = safeNumber(now.searching);
  const active = safeNumber(now.active);
  const scaleMax = Math.max(onlineFree, onlineBusy, searching, 1);
  const seriesMax = Math.max(...series.map(safeNumber), 1);
  const demandTight = searching > onlineFree;

  return (
    <div className="ops-page">
      <PageHeader
        title="Live-Betrieb"
        description="Systemweite Marktplatzlage und rollende Kennzahlen der letzten 60 Minuten."
        actions={
        <div className="ops-heading-actions">
          <span className="ops-live-state"><i /> Automatisch · 9 Sek.</span>
          <button type="button" className="ops-refresh" onClick={() => load()} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'is-spinning' : ''} />
            Aktualisieren
          </button>
        </div>
      }
      />

      {error && (
        <div className="ops-stale-note" role="status">
          <AlertTriangle size={15} />
          <span>{error} Die zuletzt erfolgreichen Werte bleiben sichtbar.</span>
        </div>
      )}

      <section className="ops-metrics" aria-label="Aktueller Betriebszustand">
        <MetricCard
          label="Aktive Fahrten"
          value={formatInteger(active)}
          detail="angenommen, angekommen oder unterwegs"
          icon={Route}
        />
        <MetricCard
          label="Fahrer online"
          value={formatInteger(onlineTotal)}
          detail={`${formatInteger(onlineFree)} frei · ${formatInteger(onlineBusy)} unterwegs`}
          icon={Users}
        />
        <MetricCard
          label="Offene Anfragen"
          value={formatInteger(searching)}
          detail="suchen aktuell einen Fahrer"
          icon={Search}
          tone={demandTight ? 'attention' : 'default'}
        />
        <MetricCard
          label="Match-Rate"
          value={formatOptional(windowData.match_rate, integer, ' %')}
          detail="vermittelt / angefragt · 60 Min."
          icon={Activity}
          tone="brand"
        />
      </section>

      <section className="ops-primary-grid">
        <article className="ops-panel">
          <div className="ops-panel-heading">
            <div>
              <span>Marktplatzlage</span>
              <h2>Angebot und Nachfrage</h2>
            </div>
            <span className={`ops-balance-state ${demandTight ? 'is-attention' : ''}`}>
              {demandTight ? 'Nachfrage beobachten' : 'Angebot stabil'}
            </span>
          </div>
          <div className="ops-signals">
            <SignalRow label="Freie Fahrer" value={onlineFree} detail="sofort vermittelbar" max={scaleMax} tone="brand" />
            <SignalRow label="Fahrer unterwegs" value={onlineBusy} detail="aktive Auslastung" max={scaleMax} />
            <SignalRow label="Fahrgäste auf Suche" value={searching} detail="offene Nachfrage" max={scaleMax} tone={demandTight ? 'attention' : 'soft'} />
          </div>
          <footer className="ops-panel-footer">
            <span>Live-Sicht aus Fahrer- und Fahrtdaten</span>
            <strong>{onlineTotal > 0 ? `${Math.round((onlineFree / onlineTotal) * 100)} % der Online-Fahrer frei` : 'Keine Fahrer online'}</strong>
          </footer>
        </article>

        <aside className="ops-panel ops-alert-panel">
          <div className="ops-panel-heading">
            <div>
              <span>Servermeldungen</span>
              <h2>Operative Hinweise</h2>
            </div>
            <span className="ops-count">{alerts.length}</span>
          </div>
          <OperationalAlerts alerts={alerts} />
        </aside>
      </section>

      <section className="ops-panel ops-window-panel">
        <div className="ops-panel-heading">
          <div>
            <span>Rollendes Zeitfenster</span>
            <h2>Leistung der letzten 60 Minuten</h2>
          </div>
          <span className="ops-asof"><Clock3 size={14} /> Stand {lastUpdated ? uhrzeit(lastUpdated) : '–'}</span>
        </div>
        <div className="ops-window-grid">
          <WindowMetric label="Anfragen" value={formatInteger(windowData.requested)} />
          <WindowMetric label="Vermittelt" value={formatInteger(windowData.matched)} />
          <WindowMetric label="Storniert" value={formatInteger(windowData.cancelled)} />
          <WindowMetric label="Kein Fahrer" value={formatInteger(windowData.no_driver)} />
          <WindowMetric label="Ø Wartezeit" value={formatOptional(windowData.avg_wait_min, decimal, ' Min.')} />
          <WindowMetric label="Ø Fahrpreis" value={windowData.avg_price == null ? '–' : currency.format(Number(windowData.avg_price))} />
        </div>
      </section>

      <section className="ops-panel ops-chart-panel">
        <div className="ops-panel-heading">
          <div>
            <span>Volumenverlauf</span>
            <h2>Fahrten pro Stunde · letzte 12 Stunden</h2>
          </div>
          <span className="ops-chart-peak">Spitze {formatInteger(seriesMax)}</span>
        </div>
        <div className="ops-chart" role="img" aria-label={`Fahrten pro Stunde der letzten zwölf Stunden, Höchstwert ${seriesMax}`}>
          {series.map((count, index) => {
            const value = safeNumber(count);
            return (
              <div className="ops-bar-slot" key={index}>
                <span
                  className={`ops-bar ${index === series.length - 1 ? 'is-current' : ''}`}
                  style={{ height: `${Math.max((value / seriesMax) * 100, value > 0 ? 5 : 2)}%` }}
                  title={`${value} Fahrten`}
                />
              </div>
            );
          })}
        </div>
        <div className="ops-chart-axis"><span>vor 12 Std.</span><span>jetzt</span></div>
      </section>
    </div>
  );
}
